<template>
  <Popover v-slot="{ open }" class="flex items-center">
    <PopoverButton
      data-test="notifications-menu-button"
      :disabled="active"
      class="relative flex h-9 w-9 items-center justify-center rounded-lg focus:outline-none"
      :class="triggerClasses(open)"
    >
      <BellIcon class="h-5 w-5 rotate-[-10deg]" stroke-width="1.5" />
      <span
        v-if="unreadCount > 0"
        data-test="notifications-unread-count"
        class="absolute -top-[3px] right-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#1F1F23] px-1 text-[10px] leading-4 font-medium text-white tabular-nums"
      >
        {{ unreadCount }}
      </span>
    </PopoverButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0"
    >
      <PopoverPanel
        data-test="notifications-menu-panel"
        class="absolute top-full right-0 z-10 mt-2 w-[434px] origin-top-right overflow-hidden rounded-lg bg-white py-4 shadow-lg ring-1 ring-black/5 focus:outline-none"
      >
        <h2 class="px-3 text-lg font-semibold text-gray-900">Notifications ({{ unreadCount }})</h2>

        <div class="mt-4 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <button
              v-for="tab in tabs"
              :key="tab.value"
              type="button"
              :data-test="`notifications-menu-tab-${tab.value}`"
              class="relative px-4 pb-2 text-sm font-medium"
              :class="{
                'text-gray-900': activeTab === tab.value,
                'text-gray-400': activeTab !== tab.value
              }"
              @click="setActiveTab(tab.value)"
            >
              {{ tab.label }}
              <span
                v-if="activeTab === tab.value"
                class="absolute right-0 -bottom-px left-0 h-1 rounded-t-full bg-gray-900"
              />
            </button>
          </div>
        </div>

        <div class="mt-3 max-h-96 overflow-y-auto">
          <NotificationsList
            v-if="activeTab === NOTIFICATION_FILTER.all"
            :groups="allGroups"
            :is-loading="allIsLoading"
            variant="flat"
            empty-message="No notifications yet."
          />
          <NotificationsList
            v-else
            :groups="unreadGroups"
            :is-loading="unreadIsLoading"
            variant="flat"
            empty-message="No notifications yet."
          />
        </div>

        <router-link
          to="/notifications"
          data-test="notifications-menu-see-all"
          class="mx-4 mt-3 block rounded-md py-2 text-center text-sm font-medium text-blue-600 hover:bg-gray-50"
        >
          See all
        </router-link>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { BellIcon } from '@heroicons/vue/outline'
import { useAllNotifications, useUnreadNotifications } from '@/hooks/use-notifications'
import { NOTIFICATION_FILTER } from '@/lib/NotificationConstants'
import NotificationsList from '@/components/notifications/NotificationsList.vue'

const VARIANT_CLASSES = {
  solid: 'cursor-pointer text-gray-500 hover:bg-bluedark-50 hover:text-bluedark-700',
  nav: 'cursor-pointer hover:text-agentnoon-primary-700 text-gray-500 hover:bg-gray-50',
  white: 'cursor-pointer ring-1 ring-gray-300 bg-white text-gray-500 hover:bg-gray-50'
}

const ACTIVE_CLASSES =
  'bg-bluedark-50 text-bluedark-700 hover:bg-bluedark-50 hover:text-bluedark-700'

const activeTab = ref(NOTIFICATION_FILTER.all)

const props = defineProps({
  variant: {
    type: String,
    default: 'solid',
    validator: (value) => ['solid', 'nav', 'white'].includes(value)
  },
  active: {
    type: Boolean,
    default: false
  }
})

/**
 * Resolve the trigger button classes.
 * @param {boolean} isOpen - Whether the notifications popover is open.
 * @returns {string} Tailwind classes for the trigger button.
 */
const triggerClasses = (isOpen) => {
  if (props.active || isOpen) return ACTIVE_CLASSES
  return VARIANT_CLASSES[props.variant]
}

const { groups: allGroups, isLoading: allIsLoading } = useAllNotifications()

const {
  groups: unreadGroups,
  totalCount: unreadCount,
  isLoading: unreadIsLoading
} = useUnreadNotifications()

const setActiveTab = (filter) => {
  activeTab.value = filter
}

const tabs = computed(() => [
  { value: NOTIFICATION_FILTER.all, label: 'All' },
  { value: NOTIFICATION_FILTER.unread, label: `Unread (${unreadCount.value})` }
])
</script>
